/**
 * audit.ts — pure dependency validation + Integrity Audit over PluginRecords.
 *
 * All checks operate ONLY on recorded registry data. No check ever fabricates
 * a passing result for something it cannot verify — unverifiable checks are
 * reported as such in their detail text.
 */

import type {
  AuditCheck,
  DependencyIssue,
  IntegrityAuditReport,
  PluginRecord,
} from './models';

// ── Dependency validation ──────────────────────────────────────────────────────

export function validateDependencies(plugins: PluginRecord[]): DependencyIssue[] {
  const issues: DependencyIssue[] = [];
  const ids = new Set(plugins.map((p) => p.id));

  // Missing dependencies + broken references.
  for (const plugin of plugins) {
    for (const dep of plugin.dependencies ?? []) {
      if (typeof dep !== 'string' || dep.trim() === '') {
        issues.push({ kind: 'brokenReference', pluginId: plugin.id, detail: 'Empty dependency reference declared.' });
        continue;
      }
      if (!ids.has(dep)) {
        issues.push({
          kind: 'missingDependency',
          pluginId: plugin.id,
          detail: `Depends on "${dep}" which is not registered.`,
        });
      }
    }
  }

  // Duplicate display names (ids are unique by construction; names can clash).
  const byName = new Map<string, string[]>();
  for (const plugin of plugins) {
    const key = plugin.name.trim().toLowerCase();
    byName.set(key, [...(byName.get(key) ?? []), plugin.id]);
  }
  for (const [name, holders] of byName) {
    if (holders.length > 1) {
      for (const id of holders) {
        issues.push({ kind: 'duplicateName', pluginId: id, detail: `Name "${name}" is shared by: ${holders.join(', ')}.` });
      }
    }
  }

  // Circular dependencies (DFS over declared edges).
  for (const cycle of findCycles(plugins)) {
    issues.push({
      kind: 'circularDependency',
      pluginId: cycle[0],
      detail: `Circular dependency: ${cycle.join(' → ')} → ${cycle[0]}.`,
    });
  }

  // Unavailable services: a plugin depends on something registered but not running.
  const byId = new Map(plugins.map((p) => [p.id, p]));
  for (const plugin of plugins) {
    for (const dep of plugin.dependencies ?? []) {
      const target = byId.get(dep);
      if (target && (target.status === 'crashed' || target.status === 'offline' || target.status === 'disabled')) {
        issues.push({
          kind: 'unavailableService',
          pluginId: plugin.id,
          detail: `Dependency "${dep}" is ${target.status}.`,
        });
      }
    }
  }

  return issues;
}

/** Returns each distinct cycle once, as the ordered list of plugin ids. */
export function findCycles(plugins: PluginRecord[]): string[][] {
  const edges = new Map<string, string[]>();
  for (const p of plugins) edges.set(p.id, (p.dependencies ?? []).filter((d) => edges !== null));

  const cycles: string[][] = [];
  const seenCycleKeys = new Set<string>();
  const visiting = new Set<string>();
  const done = new Set<string>();
  const stack: string[] = [];

  const visit = (id: string): void => {
    if (done.has(id)) return;
    if (visiting.has(id)) {
      const start = stack.indexOf(id);
      if (start >= 0) {
        const cycle = stack.slice(start);
        const key = [...cycle].sort().join('|');
        if (!seenCycleKeys.has(key)) {
          seenCycleKeys.add(key);
          cycles.push(cycle);
        }
      }
      return;
    }
    visiting.add(id);
    stack.push(id);
    for (const dep of edges.get(id) ?? []) {
      if (edges.has(dep)) visit(dep);
    }
    stack.pop();
    visiting.delete(id);
    done.add(id);
  };

  for (const p of plugins) visit(p.id);
  return cycles;
}

// ── Integrity audit ────────────────────────────────────────────────────────────

export interface AuditInputs {
  plugins: PluginRecord[];
  /** Event names that have listeners on the Core bus is not introspectable; we audit what we can. */
  busPublished: number;
  busDelivered: number;
  /** Optional recorded provider health scores (0–100) keyed by provider id. */
  providerHealth?: Record<string, number>;
  /** Event Contract System™ (1.89.0): coverage findings + registry snapshot. */
  contractFindings?: string[];
  contractSnapshot?: {
    totalContracts: number;
    knownEventCount: number;
    unknownEvents: number;
    failed: number;
    validated: number;
    duplicates: number;
  };
  now: number;
}

export function runIntegrityAudit(input: AuditInputs): IntegrityAuditReport {
  const { plugins, now } = input;
  const issues = validateDependencies(plugins);
  const checks: AuditCheck[] = [];
  const fixes: string[] = [];
  const warnings: string[] = [];

  const engines = plugins.filter((p) => p.type === 'coreEngine');
  const modules = plugins.filter((p) => p.type === 'module');

  const push = (id: string, label: string, passed: boolean, detail: string, fix?: string) => {
    checks.push({ id, label, passed, detail });
    if (!passed && fix) fixes.push(fix);
  };

  push(
    'engines-registered',
    'Core engines registered',
    engines.length > 0,
    engines.length > 0 ? `${engines.length} engine(s) discovered via Core registry.` : 'No engines discovered — has roamerCore.initialize() run?',
    'Call roamerCore.initialize() during app bootstrap.',
  );
  push(
    'modules-registered',
    'Modules registered',
    modules.length > 0,
    modules.length > 0 ? `${modules.length} module(s) discovered via Core registry.` : 'No modules discovered — module attachToCore() calls may be missing.',
    'Call each module\'s attachToCore(roamerCore) during bootstrap.',
  );

  const circular = issues.filter((i) => i.kind === 'circularDependency');
  push('no-circular', 'No circular dependencies', circular.length === 0,
    circular.length === 0 ? 'No cycles among declared dependencies.' : circular.map((c) => c.detail).join(' '),
    'Break the declared dependency cycle.');

  const missing = issues.filter((i) => i.kind === 'missingDependency' || i.kind === 'brokenReference');
  push('deps-satisfied', 'Dependencies satisfied', missing.length === 0,
    missing.length === 0 ? 'All declared dependencies resolve to registered plugins.' : missing.map((m) => `${m.pluginId}: ${m.detail}`).join(' '),
    'Register the missing dependency or remove the stale declaration.');

  const duplicates = issues.filter((i) => i.kind === 'duplicateName');
  push('no-duplicates', 'No duplicate identifiers', duplicates.length === 0,
    duplicates.length === 0 ? 'All plugin ids and names are unique.' : duplicates[0]!.detail,
    'Rename one of the colliding plugins.');

  const crashed = plugins.filter((p) => p.status === 'crashed' || p.status === 'offline');
  push('all-healthy', 'All plugins healthy', crashed.length === 0,
    crashed.length === 0 ? 'No plugin is crashed or offline.' : `Unhealthy: ${crashed.map((p) => p.id).join(', ')}.`,
    'Restart or investigate the failed plugins from the Engine/Module Manager.');

  const incompleteMeta = plugins.filter((p) => p.name.trim() === '' || p.version.trim() === '');
  push('metadata-complete', 'Metadata complete', incompleteMeta.length === 0,
    incompleteMeta.length === 0 ? 'Every plugin reports id, name and version.' : `Missing name/version: ${incompleteMeta.map((p) => p.id).join(', ')}.`,
    'Populate name/version on the adapter or provider meta.');

  const staleHeartbeat = plugins.filter(
    (p) => p.status === 'running' && p.lastHeartbeatAt !== null && now - p.lastHeartbeatAt > 24 * 60 * 60_000,
  );
  push('heartbeat-active', 'Heartbeats active', staleHeartbeat.length === 0,
    staleHeartbeat.length === 0
      ? 'No running plugin has a heartbeat older than 24h.'
      : `Stale heartbeat (>24h): ${staleHeartbeat.map((p) => p.id).join(', ')}.`,
    'Refresh the registry or investigate why the plugin stopped emitting.');

  push('bus-healthy', 'Event bus healthy',
    input.busDelivered >= 0 && input.busPublished >= input.busDelivered * 0 /* sanity: counters recorded */,
    `${input.busPublished} published / ${input.busDelivered} delivered since Core start.`);

  const unhealthyProviders = Object.entries(input.providerHealth ?? {}).filter(([, score]) => score < 50);
  push('providers-healthy', 'API providers healthy', unhealthyProviders.length === 0,
    unhealthyProviders.length === 0
      ? 'No provider with a recorded health score below 50.'
      : `Low health: ${unhealthyProviders.map(([id, s]) => `${id} (${s})`).join(', ')}.`,
    'Check the API Monitor for the failing provider chain.');

  // ── Event Contract System™ checks (1.89.0) ──
  if (input.contractSnapshot) {
    const cs = input.contractSnapshot;
    const findings = input.contractFindings ?? [];
    const missing = findings.filter((s) => s.startsWith('Missing contract'));
    const versionIssues = findings.filter((s) => s.startsWith('Version mismatch'));

    push('contracts-coverage', 'Every published event registered', missing.length === 0,
      missing.length === 0
        ? `${cs.totalContracts}/${cs.knownEventCount} known events have a registered contract.`
        : missing.join(' '),
      'Add a contract file under services/core/events/contracts/ and register it.');
    push('contracts-known-events', 'Every subscriber references a known event', cs.unknownEvents === 0,
      cs.unknownEvents === 0
        ? 'No publish targeted an event without a contract since Core start.'
        : `${cs.unknownEvents} publish(es) targeted events with no contract.`,
      'Register a contract for the unknown event (see Dev Center → Contracts warnings).');
    push('contracts-no-duplicates', 'No duplicate contracts', cs.duplicates === 0,
      cs.duplicates === 0 ? 'Each event has exactly one contract.' : `${cs.duplicates} duplicate registration attempt(s).`,
      'Remove the duplicate contract registration.');
    push('contracts-version-consistent', 'Contract versions consistent', versionIssues.length === 0,
      versionIssues.length === 0 ? 'All contracts are the supported generation (v1).' : versionIssues.join(' '),
      'Align the contract version with the supported generation.');
    push('contracts-validation-enabled', 'Payload validation enabled', cs.totalContracts > 0,
      cs.totalContracts > 0
        ? `Registry armed — ${cs.validated} publish(es) validated, ${cs.failed} failed.`
        : 'Contract registry is empty — publish-time validation inactive.',
      'Call registerAllEventContracts() during bootstrap.');
  }

  // Honest unverifiable areas → warnings, never fake passes.
  warnings.push('Bus subscriptions are private to Core — per-plugin subscribed events are audited only where declared.');
  const unavailable = issues.filter((i) => i.kind === 'unavailableService');
  for (const u of unavailable) warnings.push(`${u.pluginId}: ${u.detail}`);

  const passed = checks.filter((c) => c.passed).length;
  const healthPct = checks.length === 0 ? 0 : Math.round((passed / checks.length) * 100);

  return {
    ranAt: now,
    healthPct,
    checks,
    warnings,
    recommendedFixes: [...new Set(fixes)],
    pluginCount: plugins.length,
  };
}
